import { normalizeBroadcastMrkdwn } from "./mrkdwn";
import type { ComposerDraft, DraftDocument, MessageSection } from "./types";

const broadcastMarkerPattern = /\{\{BCHANNEL_BROADCAST:(?:channel|here)\}\}/gi;

export function isPingSection(section: MessageSection) {
  return section.type === "text" && /^\s*\{\{BCHANNEL_BROADCAST:(?:channel|here)\}\}\s*$/i.test(section.mrkdwn);
}

export function appendBeforeTrailingPing(sections: MessageSection[], section: MessageSection) {
  const next = [...sections];
  const last = sections.at(-1);
  const index = last && isPingSection(last) ? sections.length - 1 : sections.length;
  next.splice(index, 0, section);
  return next;
}

export function normalizeComposerPing(document: DraftDocument, ping: ComposerDraft["ping"]): DraftDocument {
  if (ping === "none") return document;
  const marker = `{{BCHANNEL_BROADCAST:${ping}}}`;
  const sections: MessageSection[] = [];

  for (const section of document.sections) {
    if (section.type !== "text") {
      sections.push(section);
      continue;
    }
    const mrkdwn = normalizeBroadcastMrkdwn(section.mrkdwn, ping);
    if (isPingSection({ ...section, mrkdwn })) {
      // Legacy standalone ping row: fold its marker inline at the end of the
      // preceding text section so the ping can live anywhere in the message.
      const previous = sections.at(-1);
      if (previous && previous.type === "text") {
        sections[sections.length - 1] = {
          ...previous,
          mrkdwn: `${previous.mrkdwn.replace(/\s+$/, "")}\n${marker}`,
        };
      } else {
        sections.push({ ...section, mrkdwn: marker });
      }
      continue;
    }
    let count = 0;
    const inline = mrkdwn.replace(broadcastMarkerPattern, () => {
      count += 1;
      return count === 1 ? marker : "";
    });
    sections.push({ ...section, mrkdwn: inline.replace(/[ \t]{2,}/g, " ") });
  }

  if (!sections.some((section) => section.type === "text" && hasBroadcastMarker(section.mrkdwn))) {
    let lastTextIndex = -1;
    for (let index = sections.length - 1; index >= 0; index -= 1) {
      if (sections[index]?.type === "text") {
        lastTextIndex = index;
        break;
      }
    }
    const lastText = sections[lastTextIndex];
    if (lastText?.type === "text") {
      sections[lastTextIndex] = { ...lastText, mrkdwn: `${lastText.mrkdwn.replace(/\s+$/, "")}\n${marker}` };
    } else {
      sections.push({ id: crypto.randomUUID(), type: "text", mrkdwn: marker });
    }
  }
  return { ...document, sections };
}

function hasBroadcastMarker(value: string) {
  return /{{BCHANNEL_BROADCAST:(?:channel|here)}}/i.test(value);
}
